/**
 * Comic storyboarding V2: reader bands, reader cards, dialogue caps and act budget.
 * Pure config. No I/O, no env, no runtime imports.
 */

export type ReaderBand = 'T' | 'E' | 'M' | 'O' | 'W' | 'G';
/** Youngest to oldest. */
export const READER_BAND_ORDER: readonly ReaderBand[] = ['T', 'E', 'M', 'O', 'W', 'G'];
export type ReaderCardId = 'read-aloud' | 'shared' | 'read-alone';
export type StoryAct = 'setup' | 'inciting' | 'rising' | 'midpoint' | 'escalation' | 'climax' | 'resolution';

export interface DialogueCaps {
  balloonsPerPanel: number;
  wordsPerBalloon: number;
  wordsPerPage: number;
  captionsPerPage: number;
}

function parseAge(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Youngest cast member aged 3-13 sets the band; under-3 only if no 3-13; all 14+ is G; unknown ignored. */
export function inferReaderBand(characters: { age?: unknown }[]): ReaderBand {
  const ages = (characters ?? []).map((c) => parseAge(c?.age)).filter((a): a is number => a !== null);
  if (ages.length === 0) return 'M';
  const kids = ages.filter((a) => a >= 3 && a < 14);
  if (kids.length > 0) {
    const a = Math.min(...kids);
    if (a < 4) return 'T';
    if (a < 6) return 'E';
    if (a < 8) return 'M';
    if (a < 11) return 'O';
    return 'W';
  }
  if (ages.some((a) => a < 3)) return 'T';
  return 'G';
}

export function readerCardOf(band: ReaderBand): ReaderCardId {
  if (band === 'T' || band === 'E') return 'read-aloud';
  if (band === 'M') return 'shared';
  return 'read-alone';
}

export const READER_CARD_PROSE: Record<ReaderCardId, string> = {
  'read-aloud': [
    'Reader: a parent reads this aloud to a small child, so every line must sound good spoken.',
    'Complexity: one clear want, one problem at a time, few characters, simple cause and effect.',
    'Peril: only gentle, quickly fixed worries; the problem is silly or grumpy, never truly menacing.',
    'Humour: sounds, slapstick, reversals and repeat-with-a-twist.',
    'Agency: the child has the idea and does the last, deciding action.',
    'Dialogue: very short, concrete everyday words; a refrain the child can join in; pictures carry the rest.',
  ].join('\n'),
  shared: [
    'Reader: a child reads with a grown-up beside them, taking turns.',
    'Complexity: one want with a small personal feeling behind it; a few set pieces, each leading to the next.',
    'Peril: a comic villain and clearly safe danger, with one real low moment.',
    'Humour: absurd logic, simple wordplay and character comedy.',
    'Agency: the child plans and persuades, and fails once or twice before it works.',
    'Dialogue: short sentences, one new word per set piece, a distinct voice for each speaker.',
  ].join('\n'),
  'read-alone': [
    'Reader: the reader reads alone and does not want to be talked down to.',
    'Complexity: a want plus an inner need; a real antagonist with a motive; twists that are fair.',
    'Peril: real stakes and a real low point, never cruel; no harm to the cast.',
    'Humour: puns, banter, running gags, deadpan and light genre play.',
    'Agency: the hero makes a hard choice with a cost, owns it, and earns the climax with something gained earlier.',
    'Dialogue: natural speech with a distinct voice per character; callbacks instead of chants.',
  ].join('\n'),
};

const G_TONE_NOTE = 'Tone: warm family comedy and sentiment, written for a grown-up giftee.';
const MIXED_AGES_SENTENCE =
  'Each person acts their real age. Side characters may carry a joke for an older sibling; the plot never depends on it.';

export function readerCardText(band: ReaderBand): string {
  const parts = [READER_CARD_PROSE[readerCardOf(band)]];
  if (band === 'G') parts.push(G_TONE_NOTE);
  parts.push(MIXED_AGES_SENTENCE);
  return parts.join('\n');
}

const BAND_AGE_LABEL: Record<ReaderBand, string> = {
  T: '2–3', E: '4–5', M: '6–7', O: '8–10', W: '11–13', G: '14+',
};

export function bandAgeLabel(band: ReaderBand): string {
  return BAND_AGE_LABEL[band];
}

const CARD_SUMMARY: Record<ReaderCardId, string> = {
  'read-aloud': 'a small child with a parent reading aloud: one clear want, gentle peril, silly humour, very short lines',
  shared: 'a child sharing the book with a grown-up: one want, safe danger, simple wordplay, short lines',
  'read-alone': 'an independent reader: a want with an inner need, real stakes, wit, natural dialogue',
};

export function readerCardSummary(band: ReaderBand): string {
  const base = `Written for ages ${bandAgeLabel(band)}: ${CARD_SUMMARY[readerCardOf(band)]}`;
  return band === 'G' ? `${base}; warm family comedy for a grown-up gift` : base;
}

export function dialogueCapsFor(band: ReaderBand): DialogueCaps {
  switch (band) {
    case 'T': return { balloonsPerPanel: 1, wordsPerBalloon: 6, wordsPerPage: 15, captionsPerPage: 1 };
    case 'E': return { balloonsPerPanel: 2, wordsPerBalloon: 8, wordsPerPage: 25, captionsPerPage: 1 };
    case 'M': return { balloonsPerPanel: 2, wordsPerBalloon: 12, wordsPerPage: 35, captionsPerPage: 1 };
    default: return { balloonsPerPanel: 3, wordsPerBalloon: 18, wordsPerPage: 60, captionsPerPage: 1 };
  }
}

/** Setup-to-payoff pair count range: 1-3 read-aloud, 2-4 otherwise. */
export function payoffRangeFor(band: ReaderBand): [number, number] {
  return readerCardOf(band) === 'read-aloud' ? [1, 3] : [2, 4];
}

/** Max consecutive pages one set piece may run: 3, or 4 for read-alone. */
export function maxSetPieceRun(band: ReaderBand): number {
  return readerCardOf(band) === 'read-alone' ? 4 : 3;
}

export function isCjkLanguage(lang: string): boolean {
  const base = String(lang ?? '').toLowerCase().split(/[-_]/)[0];
  return base === 'ja' || base === 'zh' || base === 'ko';
}

/** Page windows per act, inclusive. The +/-1 tolerance is applied by the lints, not stored here. */
export const COMIC_ACT_BUDGET: Record<8 | 16 | 24, Record<StoryAct, [number, number]>> = {
  8: {
    setup: [1, 1], inciting: [2, 2], rising: [3, 4], midpoint: [5, 5],
    escalation: [5, 5], climax: [6, 6], resolution: [7, 8],
  },
  16: {
    setup: [1, 2], inciting: [3, 3], rising: [4, 7], midpoint: [8, 8],
    escalation: [9, 12], climax: [13, 14], resolution: [15, 16],
  },
  24: {
    setup: [1, 2], inciting: [3, 3], rising: [4, 11], midpoint: [12, 12],
    escalation: [13, 18], climax: [19, 21], resolution: [22, 24],
  },
};

/** Picture-book narration words per page, by reader band (middle pages). */
export const PICTURE_BOOK_WORDS: Record<ReaderBand, { min: number; max: number }> = {
  T: { min: 5, max: 15 },
  E: { min: 15, max: 35 },
  M: { min: 30, max: 60 },
  O: { min: 60, max: 110 },
  W: { min: 80, max: 130 },
  G: { min: 40, max: 80 },
};

export type NarrationPosition = 'first' | 'middle' | 'last';

/**
 * Picture-book mode: words of read-aloud narration per page for a band (PICTURE_BOOK_WORDS). The first and last story
 * page are shorter: min and max x 0.7, rounded. Used only by picture-book mode (comics never call it).
 */
export function narrationWordsFor(band: ReaderBand, position: NarrationPosition = 'middle'): { min: number; max: number } {
  const w = PICTURE_BOOK_WORDS[band];
  return position === 'middle' ? { ...w } : { min: Math.round(w.min * 0.7), max: Math.round(w.max * 0.7) };
}

const NARRATION_SENTENCE_END = /[.!?…。！？]["'”’»)\]」』）]*(?=\s|$)/g;

/**
 * Picture-book narration fitted to the band's max: text within the cap is returned whole (whitespace collapsed);
 * longer text is cut after the last sentence that still fits; a first sentence over the cap is kept whole (never cut
 * mid-sentence). CJK counts characters at twice the word cap.
 */
export function fitNarrationToBand(text: string, band: ReaderBand, cjk = false, position: NarrationPosition = 'middle'): string {
  const t = String(text ?? '').trim().replace(/\s+/g, ' ');
  const max = narrationWordsFor(band, position).max * (cjk ? 2 : 1);
  const units = (s: string) => (cjk ? Array.from(s.replace(/\s+/g, '')).length : s.split(' ').filter(Boolean).length);
  if (!t || units(t) <= max) return t;
  let best = '';
  let first = '';
  for (const m of t.matchAll(NARRATION_SENTENCE_END)) {
    const head = t.slice(0, (m.index ?? 0) + m[0].length).trim();
    if (!first) first = head;
    if (units(head) <= max) best = head;
    else break;
  }
  return best || first || t;
}
